// File: src/contexts/LayoutContext.tsx
import React, { createContext, useState, useContext, ReactNode, JSX, useMemo, useCallback } from 'react';

interface LayoutContextType {
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
  headerDetail: string | null;
  setHeaderDetail: (detail: string | null) => void;
  pageIcon: JSX.Element | null;
  setPageIcon: (icon: JSX.Element | null) => void;
}

export const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [pageTitle, _setPageTitle] = useState<string | null>(null);
  const [headerDetail, _setHeaderDetail] = useState<string | null>(null);
  const [pageIcon, _setPageIcon] = useState<JSX.Element | null>(null);

  // --- FIX ---
  // The state-setting functions are now wrapped in useCallback with an empty
  // dependency array. This ensures they have a stable identity and won't be
  // re-created on every render, which prevents the infinite loop in components
  // that use them as useEffect dependencies (like PaymentSuccessPage).
  const setPageTitle = useCallback((title: string | null) => {
    _setPageTitle(title);
  }, []);

  const setHeaderDetail = useCallback((detail: string | null) => {
    _setHeaderDetail(detail);
  }, []);

  const setPageIcon = useCallback((icon: JSX.Element | null) => {
    _setPageIcon(icon);
  }, []);
  // --- END FIX ---

  const value = useMemo(() => ({
    pageTitle, setPageTitle,
    headerDetail, setHeaderDetail,
    pageIcon, setPageIcon,
  }), [pageTitle, headerDetail, pageIcon, setPageTitle, setHeaderDetail, setPageIcon]); // Add memoized functions to dependency array

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = (): LayoutContextType => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};